import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import EmojiPicker from "emoji-picker-react";
import { db } from "../../Hooks/FirebaseConfig";
import {
  addDoc,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import ChatSidebar from "./ChatSidebar";
import Cookies from "js-cookie";
import { sendMessageBack } from "../../apis/api/ChatApi";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import "./chat.css";

function getMsgMillis(m) {
  if (m._localTime != null) return m._localTime;
  if (m.createdAt?.toDate) return m.createdAt.toDate().getTime();
  if (m.createdAt?.seconds != null) return m.createdAt.seconds * 1000;
  return 0;
}

const Chat = () => {
  const [showPicker, setShowPicker] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  const pickerRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [pendingOutgoing, setPendingOutgoing] = useState([]);
  const [receiverData, setReceiverData] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const senderId = Number(Cookies.get("userID"));
  const [sidebarVersion, setSidebarVersion] = useState(0);
  const user = JSON.parse(Cookies.get("user") || "{}");
  const messagesEndRef = useRef(null);
  const sendingRef = useRef(false);

  const receiverId = receiverData?.id ? Number(receiverData.id) : null;
  const dispatch = useDispatch();
  const listenToMessages = (senderId, receiverId, callback) => {
    const messagesRef = collection(db, "messages");
    const q = query(
      messagesRef,
      where("participants", "in", [
        [senderId, receiverId],
        [receiverId, senderId],
      ]),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const messages = [];
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() });
      });

      callback(messages);
    });

    return unsubscribe;
  };

  useEffect(() => {
    if (!receiverId) return;
    const unsubscribe = listenToMessages(senderId, receiverId, setMessages);
    return () => unsubscribe();
  }, [senderId, receiverId]);

  useEffect(() => {
    setPendingOutgoing([]);
  }, [receiverId]);


   useEffect(() => {
    if (!receiverData) return;
    const id = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(id);
  }, [receiverData]);

  useEffect(() => {
    const ids = new Set(messages.map((m) => m.clientId).filter(Boolean));
    if (ids.size === 0) return;
    setPendingOutgoing((prev) => prev.filter((p) => !ids.has(p.clientId)));
  }, [messages]);

  const displayMessages = useMemo(() => {
    const confirmed = new Set(messages.map((m) => m.clientId).filter(Boolean));
    const extra = pendingOutgoing.filter((p) => !confirmed.has(p.clientId));
    return [...messages, ...extra].sort((a, b) => getMsgMillis(a) - getMsgMillis(b));
  }, [messages, pendingOutgoing]);

  useEffect(() => {
    if (!receiverData || displayMessages.length === 0) return;
    const id = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
    return () => cancelAnimationFrame(id);
  }, [receiverData, displayMessages.length, messages.length, pendingOutgoing.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onEmojiClick = (emojiData) => {
    setInputValue((prev) => prev + emojiData.emoji);
    setShowPicker(false);
  };

  const addUserIfNotExists = async (Id, profileData) => {
    let userId = Id;
    if (!Id || typeof Id !== "string") {
      userId = userId.toString();
    }

    try {
      const userRef = doc(db, "users", userId);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        await setDoc(userRef, profileData);
      } else {
      }
    } catch (error) {
      console.error("Error adding user:", error);
    }
  };

  const sendMessage = async () => {
    const text = inputValue.trim();
    if (!text || !receiverId || sendingRef.current) return;

    sendingRef.current = true;
    setIsSending(true);
    const clientId = `c_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const now = Date.now();

    setInputValue("");
    setPendingOutgoing((prev) => [
      ...prev,
      {
        id: clientId,
        clientId,
        text,
        sender_Id: senderId,
        receiver_Id: receiverId,
        participants: [senderId, receiverId],
        _localTime: now,
        pending: true,
      },
    ]);

    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });

    const payload = {
      text,
      sender_Id: senderId,
      receiver_Id: receiverId,
      participants: [senderId, receiverId],
      senderName: user.fullname || "Member",
      senderLogo: user.member_logo || null,
      createdAt: serverTimestamp(),
      clientId,
    };

    try {
      await addDoc(collection(db, "messages"), payload);
      setSidebarVersion((prev) => prev + 1);
    } catch (error) {
      console.error("Send message failed:", error);
      setInputValue(text);
      setPendingOutgoing((prev) => prev.filter((p) => p.clientId !== clientId));
    } finally {
      sendingRef.current = false;
      setIsSending(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }

    void Promise.all([
      addUserIfNotExists(receiverId, {
        receiver_Id: receiverData.id,
        full_name: receiverData.full_name,
        member_logo: receiverData.member_logo,
        company_name: receiverData.company_name,
      }),
      addUserIfNotExists(senderId, {
        receiver_Id: senderId,
        full_name: user.fullname,
        member_logo: user.member_logo,
        company_name: user.company_name,
      }),
    ]);

    dispatch(
      sendMessageBack({
        full_name: user.fullname,
        senderId,
        receiverId,
        message: text,
      })
    ).catch(() => {});
  };

  const handleDataFromSidebar = useCallback((data) => {
    setReceiverData(data);
  }, []);

    const getMsgMillisVal = (msg) => {
    if (msg?._localTime) return msg._localTime;
    if (msg?.createdAt?.toDate) return msg.createdAt.toDate().getTime();
    if (msg?.createdAt?.seconds) return msg.createdAt.seconds * 1000;
    return 0;
  };

  const formatTime = (msg) => {
    try {
     const ms = getMsgMillisVal(msg);
      if (!ms) return msg?.pending ? "Sending…" : "";
      return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return "";
    }
  };



  // Returns a readable date label for a timestamp
  const formatDateLabel = (ms) => {
    if (!ms) return null;
    const d = new Date(ms);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  };

  // Group threshold: 3 minutes of silence = time gap marker
  const GAP_MS = 3 * 60 * 1000;

  return (
    <>
      <motion.div
        className="cm-wrap"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 0.68, 0, 1.2] }}
      >
        <div className={`cm-shell ${receiverData ? 'cm-has-active-chat' : 'cm-no-active-chat'}`}>
          <ChatSidebar
            version={sidebarVersion}
            sendData={handleDataFromSidebar}
          />

          <div className="cm-main">
            <div className="cm-head">
              <div className="cm-peer">
                {receiverData && (
                  <button
                    className="cm-back-btn"
                    type="button"
                    onClick={() => setReceiverData(null)}
                    aria-label="Back to conversations"
                  >
                    <i className="bx bx-left-arrow-alt" />
                  </button>
                )}
                <img
                  src={
                    receiverData?.member_logo ||
                    `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`
                  }
                  className="cm-peer-avatar"
                  alt="avatar"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src =
                      `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`;
                  }}
                />
                <div className="cm-peer-meta">
                  <div className="cm-peer-name">
                    {receiverData ? receiverData.full_name : "Chat"}
                  </div>
                  <div className="cm-peer-sub">
                    {receiverData ? receiverData.company_name : "Select a member to start chatting"}
                  </div>
                </div>
              </div>

              <div className="cm-head-actions">
                <button className="cm-icon-btn" type="button" onClick={() => setShowPicker(false)} aria-label="Info">
                  <i className="bx bx-info-circle"></i>
                </button>
              </div>
            </div>

            <div className="cm-body">
              {!receiverData ? (
                               <div className="cm-welcome">
                  <div className="cm-welcome-visual">
                    <div className="cm-welcome-bubble cm-welcome-bubble--left">
                      <span>Hey! 👋</span>
                    </div>
                    <div className="cm-welcome-avatar-ring">
                      <i className="bx bxs-message-rounded-dots" />
                    </div>
                    <div className="cm-welcome-bubble cm-welcome-bubble--right">
                      <span>Start chatting</span>
                    </div>
                  </div>
                  <h2 className="cm-welcome-title">Your messages</h2>
                  <p className="cm-welcome-sub">
                    Connect with members across the network. Select a recent chat or start a new one.
                  </p>
                  <div className="cm-welcome-hints">
                    <div className="cm-welcome-hint">
                      <i className="bx bx-search-alt" />
                      <span>Pick from recent chats on the left</span>
                    </div>
                    <div className="cm-welcome-hint">
                      <i className="bx bx-edit-alt" />
                      <span>Click <strong>New Chat</strong> to message any member</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="cm-messages">
                  {(() => {
                    const items = [];
                    let lastDateLabel = null;

                    displayMessages.forEach((msg, i) => {
                      const mine = msg.sender_Id === senderId;
                      const ms = getMsgMillisVal(msg);
                      const prev = displayMessages[i - 1];
                      const next = displayMessages[i + 1];
                      const prevMs = prev ? getMsgMillisVal(prev) : 0;
                      const nextMs = next ? getMsgMillisVal(next) : 0;

                      // ── Date separator ──
                      const dateLabel = formatDateLabel(ms);
                      if (dateLabel && dateLabel !== lastDateLabel) {
                        lastDateLabel = dateLabel;
                        items.push(
                          <div key={`date-${ms}`} className="cm-date-sep">
                            <span>{dateLabel}</span>
                          </div>
                        );
                      }

                      // ── Grouping logic ──
                      const prevSame = prev && prev.sender_Id === msg.sender_Id && (ms - prevMs) < GAP_MS;
                      const nextSame = next && next.sender_Id === msg.sender_Id && (nextMs - ms) < GAP_MS;
                      const isLast = !nextSame; // last in a consecutive group

                      // ── Time gap spacer (3+ min break between messages) ──
                      const hasGap = prev && (ms - prevMs) >= GAP_MS;

                      // Avatar: only show on last message of a theirs-group
                      const showAvatar = !mine && isLast;
                      // Bubble tail: only on the last message of group
                      const tailClass = isLast ? (mine ? " cm-bubble--tail-mine" : " cm-bubble--tail-theirs") : "";
                      // Reduce top gap for consecutive messages from same sender
                      const compactClass = prevSame ? " cm-msg--compact" : "";
                      const gapClass = hasGap ? " cm-msg--gap" : "";

                      items.push(
                        <div
                          key={msg.id || msg.clientId}
                          className={`cm-msg ${mine ? "mine" : "theirs"}${msg.pending ? " cm-msg-pending" : ""}${compactClass}${gapClass}`}
                        >
                          {/* Avatar placeholder to keep alignment even when hidden */}
                          {!mine && (
                            <div className="cm-msg-avatar-slot">
                              {showAvatar && (
                                <img
                                  src={receiverData?.member_logo || `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`}
                                  className="cm-msg-avatar"
                                  alt=""
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`;
                                  }}
                                />
                              )}
                            </div>
                          )}
                          <div className={`cm-bubble${tailClass}`}>
                            <div className="cm-text">{msg.text}</div>
                            <div className="cm-time">{formatTime(msg)}</div>
                          </div>
                        </div>
                      );
                    });

                    return items;
                  })()}
                  <div ref={messagesEndRef} aria-hidden="true" />
                </div>
              )}
            </div>

            <div className="cm-foot">
              <div className="cm-input">
                <button
                  className="cm-emoji"
                  type="button"
                  onClick={() => setShowPicker((v) => !v)}
                  disabled={!receiverData}
                  aria-label="Emoji"
                >
                  <i className="bx bx-smile"></i>
                </button>

                {showPicker && receiverData && (
                  <div ref={pickerRef} className="cm-emoji-pop">
                    <EmojiPicker onEmojiClick={onEmojiClick} />
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="text"
                  className="cm-textbox"
                  placeholder={receiverData ? "Type a message..." : "Select a conversation..."}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={!receiverData || isSending}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <button
                  className="cm-send"
                  type="button"
                  onClick={sendMessage}
                  disabled={!receiverData || isSending}
                >
                  <i className="bx bx-send"></i>
                  <span>Send</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Chat;
